// Direct text replacement helpers for class-variable references.
// Example: "var(--cardRule)" -> ".card { ... }".
import { parseClassVariableRule } from '../parser'
import { readAdjacentVarReferenceAt } from './adjacent-var'
import type { ClassTransformOptions } from './types'

// Replace direct class variable references in generic source text.
export function replaceClassVariableReferencesInText(source: string, classVariables: Record<string, string>): string {
  let output = ''
  let index = 0

  // Scan and replace only `var(--name)` references that map to class variables.
  while (index < source.length) {
    const token = readAdjacentVarReferenceAt(source, index)
    // Keep raw character when no reference starts here.
    if (!token) {
      output += source[index]
      index += 1
      continue
    } else {
      // Continue class variable lookup for parsed var token.
    }

    const classValue = (token.reference.objectKey || token.reference.arrayIndex !== null)
      ? undefined
      : classVariables[token.reference.variableName]
    // Keep raw var reference when variable is not class-style.
    if (!classValue) {
      output += token.raw
      index = token.reference.end
      continue
    } else {
      // Insert class-variable raw value in generic text.
      output += token.hasAmpersandPrefix ? `&${classValue}` : classValue
      index = token.reference.end
    }
  }

  // Return text with direct class variable replacements.
  return output
}

// Resolve class variable token into selector/body rule when possible.
export function resolveClassVariableRule(
  variableName: string,
  options: ClassTransformOptions,
): { selector: string; body: string } | null {
  const classValue = options.classVariables[variableName]
  // Return null when variable is not a class-style variable.
  if (!classValue) {
    return null
  } else {
    // Return parsed class rule when class variable exists.
    return parseClassVariableRule(classValue)
  }
}
